import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? `${window.location.origin}/api`
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

const authApi = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await authApi.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (email, password) => {
    const response = await authApi.post('/auth/login', { email, password });
    // Premortem-D: if the backend returns an MFA challenge, surface it
    // instead of committing the user. LoginPage routes the OTP step.
    if (response.data?.mfa_required) {
      return { mfaRequired: true, challengeToken: response.data.mfa_challenge_token, stage: response.data.mfa_stage };
    }
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  }, []);

  const loginMfa = useCallback(async ({ challengeToken, otp, backupCode }) => {
    const response = await authApi.post('/auth/login/mfa', {
      challenge_token: challengeToken,
      otp: otp || undefined,
      backup_code: backupCode || undefined,
    });
    const { user: userData } = response.data;
    setUser(userData);
    return { user: userData, usedBackupCode: response.data.used_backup_code };
  }, []);

  // ─── Mandatory MFA enrollment (pre-session, X-MFA-Token transport) ───
  const mfaSetup = useCallback(async (challengeToken) => {
    const r = await authApi.post('/auth/mfa/setup', null, {
      headers: { 'X-MFA-Token': challengeToken },
    });
    return r.data; // { secret, provisioning_uri, issuer, algorithm }
  }, []);

  const mfaConfirm = useCallback(async (challengeToken, otp) => {
    const r = await authApi.post('/auth/mfa/confirm', { otp }, {
      headers: { 'X-MFA-Token': challengeToken },
    });
    return r.data; // { verified, recovery_codes, acknowledgment_required }
  }, []);

  const mfaAcknowledge = useCallback(async (challengeToken) => {
    const r = await authApi.post('/auth/mfa/enroll/acknowledge', null, {
      headers: { 'X-MFA-Token': challengeToken },
    });
    const { user: userData } = r.data;
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, name, agency_name) => {
    const response = await authApi.post('/auth/register', {
      email, password, name, agency_name,
    });
    // Mandatory MFA: registration returns an enrollment challenge, not a
    // session. The caller routes into the enrollment wizard.
    if (response.data?.mfa_required) {
      return {
        mfaRequired: true,
        challengeToken: response.data.mfa_challenge_token,
        stage: response.data.mfa_stage,
      };
    }
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.post('/auth/logout');
    } catch (e) {
      // Logout is local-first: even if the server call fails (network down,
      // 401 because the session already expired, etc.), we still clear local
      // user state so the UI moves on. Log for debugging but don't bubble.
      console.warn('[Auth] logout call failed, clearing local session anyway:', e?.message);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user, loading, login, loginMfa, register, logout,
      mfaSetup, mfaConfirm, mfaAcknowledge,
      refreshUser: fetchUser,
    }),
    [user, loading, login, loginMfa, register, logout, mfaSetup, mfaConfirm, mfaAcknowledge, fetchUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
